import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from './useAdminApi';

/* ------------------------------------------------------------------ */
/* EDI Orders & Invoicing — settings + review-queue hooks               */
/* ------------------------------------------------------------------ */

export interface EdiSettings {
  enabled: boolean;
  inbound_address: string;
  sender_allowlist: string[];
  order_notify_list: string[];
  auto_submit: boolean;
  location_aliases: Record<string, string>;
  /** Chain gateways: PO store key → member STORE customer uuid. */
  member_aliases: Record<string, string>;
  extraction_hints: string | null;
}

export interface EdiLocationOption {
  id: string;
  display_name: string | null;
  location_code: string | null;
  city?: string | null;
}

/** A chain gateway's enabled store customer (own SF/QBO billing account). */
export interface EdiMemberOption {
  customer_id: string;
  name: string;
  qbo_customer_id: string;
  /** The chain's own store number — what the PO prefix matches on. */
  store_code?: string | null;
  location_id?: string | null;
  city?: string | null;
  delivery_schedule?: unknown;
}

export interface EdiSettingsResponse {
  ok: true;
  settings: EdiSettings | null;
  locations: EdiLocationOption[];
  members: EdiMemberOption[];
}

export function useEdiSettings(customerId: string) {
  return useQuery({
    queryKey: ['admin', 'edi-settings', customerId],
    queryFn: () =>
      adminFetch<EdiSettingsResponse>(
        `admin-edi-settings?customer_id=${encodeURIComponent(customerId)}`,
      ),
    staleTime: 30_000,
  });
}

export function useSaveEdiSettings(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (settings: Partial<EdiSettings> | null) =>
      adminFetch<{ ok: true; settings: EdiSettings | null }>('admin-edi-settings', {
        method: 'POST',
        body: JSON.stringify({ customer_id: customerId, settings }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'edi-settings', customerId] });
    },
  });
}

/* ------------------------------------------------------------------ */
/* Intake rows                                                          */
/* ------------------------------------------------------------------ */

export interface ParsedPoLine {
  item_code: string | null;
  description: string;
  qty: number;
  unit_price: number | null;
}

export interface ParsedPo {
  po_number: string | null;
  store_name: string | null;
  store_number: string | null;
  account_number?: string | null;
  ship_to: { line1: string | null; city: string | null; state: string | null; zip: string | null } | null;
  requested_delivery_date: string | null;
  lines: ParsedPoLine[];
  notes: string | null;
}

export interface MatchedLine {
  line_number: number;
  item_code: string | null;
  description: string;
  qty: number;
  unit_price: number | null;
  catalog_item_id: string | null;
  matched_label: string | null;
  matched_sku: string | null;
  method: 'alias' | 'sku' | 'search' | 'manual' | null;
}

export type EdiIntakeStatus =
  | 'received'
  | 'processing'
  | 'needs_review'
  | 'submitted'
  | 'rejected'
  | 'failed'
  | 'ignored';

export interface EdiIntakeRow {
  id: string;
  customer_id: string | null;
  /** Chain intakes: the resolved STORE customer the order submits under. */
  member_customer_id?: string | null;
  customer_name: string | null;
  from_email: string | null;
  subject: string | null;
  received_at: string | null;
  po_number: string | null;
  status: EdiIntakeStatus;
  hold_reason: string | null;
  order_id: string | null;
  error: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  parsed: ParsedPo | null;
  matched: MatchedLine[] | null;
  customer_location_id: string | null;
  attachments: Array<{ path: string; filename: string; media_type: string }> | null;
}

export function useEdiIntakeList(opts: { status: string; customerId?: string }) {
  const params = new URLSearchParams({ status: opts.status });
  if (opts.customerId) params.set('customer_id', opts.customerId);
  return useQuery({
    queryKey: ['admin', 'edi-intake', opts.status, opts.customerId ?? 'all'],
    queryFn: () => adminFetch<{ ok: true; rows: EdiIntakeRow[] }>(`admin-edi-intake?${params}`),
    staleTime: 15_000,
    refetchInterval: 60_000,
  });
}

export interface EdiIntakeDetailResponse {
  ok: true;
  intake: EdiIntakeRow & { raw_text: string | null; resend_email_id: string | null };
  customer: { id: string; name: string; delivery_schedule?: unknown } | null;
  locations: Array<EdiLocationOption & { delivery_schedule?: unknown }>;
  /** Non-empty = chain gateway: the review page picks a STORE, not a location. */
  members: EdiMemberOption[];
  attachments: Array<{ path: string; filename: string; media_type: string; url: string | null }>;
}

export function useEdiIntakeDetail(intakeId: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'edi-intake-detail', intakeId],
    queryFn: () =>
      adminFetch<EdiIntakeDetailResponse>(
        `admin-edi-intake?id=${encodeURIComponent(intakeId ?? '')}`,
      ),
    enabled: !!intakeId,
    staleTime: 10_000,
  });
}

export interface EdiCatalogHit {
  catalog_item_id: string;
  label: string;
  sku: string | null;
  category: string | null;
}

export async function ediCatalogSearch(term: string): Promise<EdiCatalogHit[]> {
  if (!term.trim()) return [];
  const res = await adminFetch<{ ok: true; items: EdiCatalogHit[] }>(
    `admin-edi-intake?catalog_search=${encodeURIComponent(term.trim())}`,
  );
  return res.items;
}

/* ------------------------------------------------------------------ */
/* Decisions                                                            */
/* ------------------------------------------------------------------ */

export interface EdiSubmitInput {
  intake_id: string;
  /** Chain gateways: the STORE customer to submit under (required there). */
  member_customer_id?: string | null;
  customer_location_id?: string;
  lines: Array<{
    catalog_item_id: string;
    quantity: number;
    item_code?: string | null;
    description?: string | null;
  }>;
  po_number?: string | null;
  requested_delivery_date?: string | null;
  delivery_notes?: string | null;
}

export interface EdiSubmitResult {
  ok: true;
  order_id: string;
  order_number: string | null;
  sf_job_id: number | null;
  sf_job_number: string | null;
  aliases_learned: number;
  idempotent_replay?: boolean;
}

export function useEdiSubmit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EdiSubmitInput) =>
      adminFetch<EdiSubmitResult>('admin-edi-decide', {
        method: 'POST',
        body: JSON.stringify({ action: 'submit', ...input }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'edi-intake'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'edi-intake-detail'] });
    },
  });
}

export function useEdiReject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { intake_id: string; reason?: string; notify_sender?: boolean }) =>
      adminFetch<{ ok: true; rejected: true; sender_notified: boolean }>('admin-edi-decide', {
        method: 'POST',
        body: JSON.stringify({ action: 'reject', ...input }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'edi-intake'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'edi-intake-detail'] });
    },
  });
}

export function useEdiReprocess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (intakeId: string) =>
      adminFetch<{ ok: boolean; queued: boolean }>('admin-edi-intake', {
        method: 'POST',
        body: JSON.stringify({ action: 'reprocess', intake_id: intakeId }),
      }),
    onSuccess: () => {
      // The background run takes a while — a delayed invalidate catches most
      // outcomes; the list also refetches on its own interval.
      setTimeout(() => {
        void qc.invalidateQueries({ queryKey: ['admin', 'edi-intake'] });
        void qc.invalidateQueries({ queryKey: ['admin', 'edi-intake-detail'] });
      }, 8_000);
    },
  });
}

/* ------------------------------------------------------------------ */
/* Labels                                                               */
/* ------------------------------------------------------------------ */

export const HOLD_REASON_LABELS: Record<string, string> = {
  no_attachment: 'No PO attachment',
  ocr_failed: 'Could not read the PO',
  location: 'Store not matched',
  line_match: 'Unmatched line items',
  duplicate_po: 'Duplicate PO number',
  review_first: 'Awaiting review',
  submit_failed: 'Submit failed',
  sender_rejected: 'Sender not allowed',
  has_comments: 'PO has comments — read first',
  price_mismatch: 'Price differs from ours',
};

export const STATUS_LABELS: Record<EdiIntakeStatus, string> = {
  received: 'Received',
  processing: 'Processing',
  needs_review: 'Needs review',
  submitted: 'Submitted',
  rejected: 'Rejected',
  failed: 'Failed',
  ignored: 'Ignored',
};

/* ------------------------------------------------------------------ */
/* Item crosswalk — the chain's item codes → our catalog                */
/* ------------------------------------------------------------------ */

export interface EdiCatalogItem {
  id: string;
  sku: string | null;
  name: string;
  sf_item_name: string | null;
  active: boolean;
  orderable: boolean;
}

export interface EdiAliasRow {
  id: string;
  alias_norm: string;
  catalog_item_id: string;
  created_from: string | null;
  created_by: string | null;
  created_at: string;
  item: EdiCatalogItem | null;
  /** The mapping points at an item that is gone or no longer orderable. */
  stale: boolean;
}

export function useEdiAliases(customerId: string, enabled = true) {
  return useQuery({
    queryKey: ['admin', 'edi-aliases', customerId],
    enabled: enabled && !!customerId,
    staleTime: 30_000,
    queryFn: () =>
      adminFetch<{ ok: true; aliases: EdiAliasRow[] }>(
        `admin-edi-aliases?customer_id=${encodeURIComponent(customerId)}`,
      ),
  });
}

export function useEdiAliasCatalogSearch(term: string) {
  return useQuery({
    queryKey: ['admin', 'edi-alias-catalog', term],
    enabled: term.trim().length >= 2,
    staleTime: 60_000,
    queryFn: () =>
      adminFetch<{ ok: true; catalog: EdiCatalogItem[] }>(
        `admin-edi-aliases?catalog_search=${encodeURIComponent(term.trim())}`,
      ),
  });
}

export function useSaveEdiAlias(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { alias: string; catalog_item_id: string }) =>
      adminFetch<{ ok: true }>('admin-edi-aliases', {
        method: 'POST',
        body: JSON.stringify({ customer_id: customerId, action: 'upsert', ...input }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'edi-aliases', customerId] });
    },
  });
}

export function useDeleteEdiAlias(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      adminFetch<{ ok: true }>('admin-edi-aliases', {
        method: 'POST',
        body: JSON.stringify({ customer_id: customerId, action: 'delete', id }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'edi-aliases', customerId] });
    },
  });
}
